/**
 * StudioPlusChat - Conversational interface for building simulation blueprints
 * 
 * Features:
 * - Chat with AI to define simulation parameters
 * - Real-time blueprint updates from conversation
 * - Manual form edit awareness
 * - Progress tracking for required fields
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Send, Bot, User, Loader2, Sparkles, CheckCircle2, AlertCircle, Edit3, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { API_BASE, getAuthHeaders } from '../../utils/api';

const FIELD_LABELS: Record<string, string> = {
  'metadata.name': 'Simulation Name',
  'metadata.theme': 'Theme',
  'metadata.primary_objective': 'Primary Objective',
  'metadata.difficulty_level': 'Difficulty Level',
  'metadata.expected_duration_steps': 'Duration Steps',
  'metadata.simulation_run_duration_min': 'Time Limit',
  'user_agent.role': 'User Role',
  'user_agent.agent_name': 'User Name',
  'user_agent.capabilities': 'User Capabilities',
  'user_agent.traits': 'User Traits',
  'user_agent.goals': 'User Goals',
  'story_skeleton.starting_context': 'Starting Context',
  'story_skeleton.key_stakeholders': 'Key Stakeholders',
  'story_skeleton.primary_conflicts': 'Key Challenges',
  'story_skeleton.possible_outcomes': 'Possible Outcomes',
  'environment.type': 'Environment Type',
  'environment.locations': 'Locations',
  'environment.rules': 'Environment Rules',
};

function formatFieldUpdate(path: string, value: any): string {
  const label = FIELD_LABELS[path] || path.split('.').pop()?.replace(/_/g, ' ') || path;
  
  if (Array.isArray(value)) {
    return `${label}: ${value.length > 0 ? value.join(', ') : '(cleared)'}`;
  }
  if (typeof value === 'number') {
    return `${label}: ${value}`;
  }
  if (typeof value === 'string') {
    const truncated = value.length > 50 ? value.substring(0, 50) + '...' : value;
    return `${label}: "${truncated}"`;
  }
  return `${label}: ${JSON.stringify(value)}`;
}

interface ConversationMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  blueprint_updates?: Record<string, any>;
  isFormEdit?: boolean;
  isFieldSummary?: boolean;
  emptyFields?: EmptyFieldInfo[];
}

interface BlueprintProgress {
  name: boolean;
  theme: boolean;
  primary_objective: boolean;
  user_role: boolean;
  has_other_agents: boolean;
  starting_context: boolean;
  primary_conflicts: boolean;
}

interface EmptyFieldInfo {
  field: string;
  label: string;
  description: string;
  impact: string;
}

interface StudioPlusSession {
  session_id: string;
  created_at: string;
  conversation_history: ConversationMessage[];
  blueprint: Record<string, any>;
  progress: BlueprintProgress;
  is_complete: boolean;
  missing_fields: string[];
  empty_optional_fields: EmptyFieldInfo[];
  message_count: number;
}

interface StudioPlusChatProps {
  onBlueprintUpdate: (blueprint: Record<string, any>) => void;
  onSessionReady: (session: StudioPlusSession) => void;
  onComplete: (isComplete: boolean) => void;
  currentBlueprint?: Record<string, any>;
  manualUpdates?: Record<string, any>;
  onManualUpdatesSent?: () => void;
}

export function StudioPlusChat({
  onBlueprintUpdate,
  onSessionReady,
  onComplete,
  currentBlueprint,
  manualUpdates,
  onManualUpdatesSent
}: StudioPlusChatProps) {
  const [session, setSession] = useState<StudioPlusSession | null>(null);
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sessionIdRef = useRef<string | null>(null);
  const lastSummaryAtCountRef = useRef<number>(0);
  
  const SUMMARY_INTERVAL = 3; // Show summary every 3 user interactions

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Initialize session on mount
  useEffect(() => {
    initializeSession();
    return () => {
      // Cleanup session on unmount
      if (sessionIdRef.current) {
        cleanupSession(sessionIdRef.current);
      }
    };
  }, []);

  // Handle manual updates from form
  useEffect(() => {
    if (manualUpdates && session && Object.keys(manualUpdates).length > 0) {
      sendManualUpdates(manualUpdates);
    }
  }, [manualUpdates, session]);

  const initializeSession = async () => {
    setIsInitializing(true);
    setError(null);
    
    try {
      const authHeaders = await getAuthHeaders();
      const response = await axios.post(
        `${API_BASE}/studio-plus/session`,
        {},
        { headers: authHeaders }
      );
      
      if (response.data.success) {
        const sessionData = response.data.data;
        sessionIdRef.current = sessionData.session_id;
        setSession(sessionData);
        setMessages(sessionData.conversation_history || []);
        onSessionReady(sessionData);
        onBlueprintUpdate(sessionData.blueprint);
        onComplete(sessionData.is_complete);
      } else {
        throw new Error(response.data.error || 'Failed to create session');
      }
    } catch (err: any) {
      console.error('Failed to initialize Studio+ session:', err);
      setError(err.message || 'Failed to start conversation');
    } finally {
      setIsInitializing(false);
    }
  };

  const cleanupSession = async (sessionId: string) => {
    try {
      const authHeaders = await getAuthHeaders();
      await axios.delete(`${API_BASE}/studio-plus/session/${sessionId}`, {
        headers: authHeaders
      });
    } catch (err) {
      console.error('Failed to cleanup session:', err);
    }
  };

  const sendManualUpdates = async (updates: Record<string, any>) => {
    if (!session) {
      return;
    }
    
    // Format updates into a human-readable message for AI
    const updateDescriptions = Object.entries(updates)
      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
      .map(([path, value]) => formatFieldUpdate(path, value));
    
    if (updateDescriptions.length === 0) {
      onManualUpdatesSent?.();
      return;
    }
    
    const formEditMessage = `[Form Edit] I updated the following fields:\n${updateDescriptions.map(d => `- ${d}`).join('\n')}`;
    
    // Add user message for form edit
    const userEditMessage: ConversationMessage = {
      role: 'user',
      content: formEditMessage,
      timestamp: new Date().toISOString(),
      isFormEdit: true
    };
    setMessages(prev => [...prev, userEditMessage]);
    setIsLoading(true);
    
    try {
      const authHeaders = await getAuthHeaders();
      const response = await axios.post(
        `${API_BASE}/studio-plus/chat`,
        {
          session_id: session.session_id,
          message: formEditMessage,
          manual_updates: updates,
          is_form_edit: true
        },
        { headers: authHeaders }
      );
      
      if (response.data.success) {
        const { message, session: updatedSession } = response.data.data;
        
        // Add AI acknowledgment response
        const assistantMessage: ConversationMessage = {
          role: 'assistant',
          content: message,
          timestamp: new Date().toISOString(),
          blueprint_updates: response.data.data.blueprint_updates
        };
        setMessages(prev => [...prev, assistantMessage]);
        
        setSession(updatedSession);
        // Pass the updated blueprint - handleBlueprintUpdate will protect in-flight fields
        onBlueprintUpdate(updatedSession.blueprint);
        onComplete(updatedSession.is_complete);
      }
      
      onManualUpdatesSent?.();
    } catch (err) {
      console.error('Failed to send manual updates:', err);
      // Remove optimistic message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || !session || isLoading) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    setIsLoading(true);
    setError(null);
    
    // Optimistically add user message
    const newUserMessage: ConversationMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newUserMessage]);
    
    try {
      const authHeaders = await getAuthHeaders();
      const response = await axios.post(
        `${API_BASE}/studio-plus/chat`,
        {
          session_id: session.session_id,
          message: userMessage
        },
        { headers: authHeaders }
      );
      
      if (response.data.success) {
        const { message, session: updatedSession } = response.data.data;
        
        // Add assistant message
        const assistantMessage: ConversationMessage = {
          role: 'assistant',
          content: message,
          timestamp: new Date().toISOString(),
          blueprint_updates: response.data.data.blueprint_updates
        };
        setMessages(prev => [...prev, assistantMessage]);
        
        // Update session and blueprint
        setSession(updatedSession);
        onBlueprintUpdate(updatedSession.blueprint);
        onComplete(updatedSession.is_complete);
        
        // Check if we should show an empty fields summary
        checkAndShowFieldSummary(updatedSession);
      } else {
        throw new Error(response.data.error || 'Failed to get response');
      }
    } catch (err: any) {
      console.error('Chat error:', err);
      setError(err.message || 'Failed to send message');
      // Remove the optimistic user message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const hasActualBlueprintUpdates = (updates?: Record<string, any>): boolean => {
    if (!updates) return false;
    return Object.entries(updates).some(([key, value]) => {
      if (value === null || value === undefined || value === '') return false;
      if (key === 'add_agent' && typeof value === 'object') return true;
      return true;
    });
  };

  const checkAndShowFieldSummary = useCallback((updatedSession: StudioPlusSession) => {
    const messageCount = updatedSession.message_count || 0;
    const emptyFields = updatedSession.empty_optional_fields || [];
    
    // Only show if we have empty fields, not complete, and enough messages since last summary
    if (
      emptyFields.length > 0 && 
      !updatedSession.is_complete &&
      messageCount > 0 &&
      messageCount - lastSummaryAtCountRef.current >= SUMMARY_INTERVAL
    ) {
      lastSummaryAtCountRef.current = messageCount;
      
      const summaryMessage: ConversationMessage = {
        role: 'system',
        content: `Here's a quick summary of optional fields you haven't filled yet. If left empty, the AI will generate or use defaults for these:`,
        timestamp: new Date().toISOString(),
        isFieldSummary: true,
        emptyFields: emptyFields.slice(0, 4) // Show max 4 fields
      };
      
      setMessages(prev => [...prev, summaryMessage]);
    }
  }, []);

  if (isInitializing) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-slate-50 p-8">
        <div className="relative">
          <div className="w-16 h-16 rounded-full bg-emerald-500 animate-pulse" />
          <Sparkles className="absolute inset-0 m-auto w-8 h-8 text-white animate-spin" style={{ animationDuration: '3s' }} />
        </div>
        <p className="mt-4 text-slate-600 font-medium">Initializing Studio+...</p>
      </div>
    );
  }

  if (error && !session) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-slate-50 p-8">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-red-600 font-medium mb-4">{error}</p>
        <button
          onClick={initializeSession}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-slate-50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900">Assistant</h3>
            <p className="text-xs text-slate-500">
              {session?.is_complete ? (
                <span className="flex items-center gap-1 text-emerald-600">
                  <CheckCircle2 className="w-3 h-3" />
                  Blueprint Ready
                </span>
              ) : (
                `${session?.missing_fields?.length || 0} fields remaining`
              )}
            </p>
          </div>
        </div>
        
        {/* Progress indicator */}
        <div className="flex items-center gap-2">
          {session?.progress && (
            <div className="flex gap-1">
              {Object.entries(session.progress).slice(0, 5).map(([key, value]) => (
                <div
                  key={key}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    value ? 'bg-emerald-500' : 'bg-slate-300'
                  }`}
                  title={key.replace(/_/g, ' ')}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <AnimatePresence initial={false}>
          {messages.map((message, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
            >
              {/* System message (field summary) */}
              {message.role === 'system' && message.isFieldSummary ? (
                <div className="w-full bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Info className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">Optional Fields Summary</span>
                  </div>
                  <p className="text-sm text-amber-700 mb-3">{message.content}</p>
                  {message.emptyFields && message.emptyFields.length > 0 && (
                    <div className="space-y-2">
                      {message.emptyFields.map((field, idx) => (
                        <div key={idx} className="bg-white/60 rounded-lg p-2 border border-amber-100">
                          <div className="text-sm font-medium text-slate-800">{field.label}</div>
                          <div className="text-xs text-slate-500">{field.description}</div>
                          <div className="text-xs text-amber-600 mt-1">If empty: {field.impact}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {/* Avatar */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                    message.role === 'user' 
                      ? message.isFormEdit ? 'bg-violet-500' : 'bg-emerald-600'
                      : 'bg-slate-700'
                  }`}>
                    {message.role === 'user' ? (
                      message.isFormEdit ? (
                        <Edit3 className="w-4 h-4 text-white" />
                      ) : (
                        <User className="w-4 h-4 text-white" />
                      )
                    ) : (
                      <Bot className="w-4 h-4 text-white" />
                    )}
                  </div>
                  
                  {/* Message bubble */}
                  <div className={`max-w-[80%] ${message.role === 'user' ? 'text-right' : ''}`}>
                    <div className={`rounded-2xl px-4 py-2.5 ${
                      message.role === 'user'
                        ? message.isFormEdit 
                          ? 'bg-violet-500 text-white rounded-br-md'
                          : 'bg-emerald-600 text-white rounded-br-md'
                        : 'bg-white text-slate-800 rounded-bl-md border border-slate-200 shadow-sm'
                    }`}>
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                    </div>
                    
                    {/* Blueprint update indicator - only show when there are actual updates */}
                    {hasActualBlueprintUpdates(message.blueprint_updates) && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-violet-600">
                        <Sparkles className="w-3 h-3" />
                        <span>Updated blueprint</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
        
        {/* Loading indicator */}
        {isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex gap-3"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 flex items-center justify-center">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 border border-slate-200 shadow-sm">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 text-violet-500 animate-spin" />
                <span className="text-sm text-slate-500">Thinking...</span>
              </div>
            </div>
          </motion.div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Error message */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-t border-red-200">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Input area */}
      <div className="p-4 border-t border-slate-200 bg-white">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef as any}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={handleKeyDown}
            placeholder={messages.length >= 100
              ? "Message limit reached (100)"
              : session?.is_complete 
                ? "Blueprint complete! You can still make changes..." 
                : "Describe your simulation scenario..."
            }
            disabled={isLoading || messages.length >= 100}
            rows={1}
            className="flex-1 bg-slate-50 border border-slate-300 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50 resize-none overflow-hidden"
            style={{ minHeight: '48px', maxHeight: '120px' }}
          />
          <button
            onClick={sendMessage}
            disabled={!inputValue.trim() || isLoading || messages.length >= 100}
            className="p-3 bg-emerald-600 hover:bg-emerald-700 rounded-xl text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
          >
            {isLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
        
        {/* Message count and completion hint */}
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs text-slate-400">{messages.length}/100 messages</span>
          {session?.is_complete && (
            <span className="text-xs text-emerald-600">
              Blueprint ready. Click Save or Launch to continue.
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default StudioPlusChat;

